import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { UnitErrorTimelinePoint } from 'services/api/useUnitErrorDetail';

type TimelineChartProps = {
  data: UnitErrorTimelinePoint[];
  height?: number;
};

/**
 * Lightweight occurrence-over-time bar chart built from MUI primitives.
 * The repo ships no charting library, so we render bars manually rather than
 * pulling in a new dependency.
 */
const TimelineChart = ({ data, height = 160 }: TimelineChartProps) => {
  const theme = useTheme();

  if (!data || data.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        No occurrences recorded.
      </Typography>
    );
  }

  const maxCount = Math.max(...data.map((point) => point.count), 1);

  return (
    <Stack direction="row" alignItems="flex-end" spacing={0.5} sx={{ height, width: '100%', overflowX: 'auto' }}>
      {data.map((point) => {
        const barHeight = Math.max((point.count / maxCount) * (height - 24), 2);
        return (
          <Tooltip key={point.date} title={`${point.date}: ${point.count}`} arrow>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '1 0 12px' }}>
              <Typography variant="caption" color="textSecondary">
                {point.count}
              </Typography>
              <Box
                sx={{
                  width: '100%',
                  minWidth: 8,
                  height: barHeight,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: '2px 2px 0 0',
                }}
              />
            </Box>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

export default TimelineChart;
